import React, { memo, useContext, useState } from "react";
import Avatar from "@mui/material/Avatar";
import { Grid2 as Grid, Typography, CardActions, Button } from "@mui/material";
import Popover from "@mui/material/Popover";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { UserContext } from "../../providers/user";
import { useMsal } from "@azure/msal-react";
import { isNativeApp, nativeLogout } from "../../infrastructure/auth";
import { appInsights } from "../../services";
import { TitleValueDisplay } from "../TitleValueDisplay";
import ToggleSwitch from "../Common/ToggleSwitch";
import { InitialAppDataContext } from "../../providers/initial-app-data";
import AustraliaFilled from "../IconComponents/AustraliaFilled";
import NewZealandFilled from "../IconComponents/NewZealandFilled";
import PageContext from "../Common/PageContext";
import { useStyles } from "./AppBar.styles";
import { JbAppBarProps } from "./types";

export const UserDetail = memo((props: JbAppBarProps) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const classes = useStyles();
	const { changeTenantCountry } = React.useContext(InitialAppDataContext);
	const [pageContext] = useContext(PageContext);
	const { userInformation, userLocation } = pageContext;
	const { countryCode, clientIp } = userInformation;

	const countryImage = countryCode.toUpperCase() === "AU" ? <AustraliaFilled /> : <NewZealandFilled />;

	const handleClose = () => {
		setAnchorEl(null);
	};
	const handleMenu = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const { instance } = useMsal();

	const logout = () => {
		// Native wrapper owns the session — delegate sign-out to the bridge
		// (the wrapper signs out of MSAL and reloads the page).
		if (isNativeApp()) {
			appInsights.clearAuthenticatedUserContext();
			nativeLogout();
			return;
		}
		instance
			.logoutRedirect({ account: instance.getActiveAccount() })
			.then(() => appInsights.clearAuthenticatedUserContext());
	};

	const switchCountry = () => {
		const country = countryCode.toUpperCase() === "AU" ? "Nz" : "Au";
		changeTenantCountry && changeTenantCountry(country);
	};

	const { graphProfile, photo } = useContext(UserContext);

	if (!graphProfile) {
		return null;
	}
	return (
		<React.Fragment>
			<Avatar src={`data:image/png;base64,${photo}`} onClick={handleMenu} />
			<Popover
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "center",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "center",
				}}
			>
				<Card style={{ width: 300 }}>
						<CardContent sx={{ py: 1, px: 2 }}>
							<Grid container direction="column" spacing={0.5}>
							<Grid container justifyContent="flex-start" alignItems="center">
								<Grid size={3}>
									<Avatar className={classes.avatarBig} src={`data:image/png;base64,${photo}`} />
								</Grid>
								<Grid size="grow">
									<Typography variant="h6" component="div">
										{graphProfile.displayName}
										<Typography variant="subtitle1" component="div">
											{graphProfile.staffId}
										</Typography>
									</Typography>
								</Grid>
							</Grid>
							{/* tenancy toggle */}
							{graphProfile?.storeNumber === 0 && (
								<Grid size={12}>
									<ToggleSwitch
										leftText="AU"
										rightText="NZ"
										currentCountry={countryCode.toUpperCase()}
										onChange={switchCountry}
									/>
								</Grid>
							)}
							<Grid container justifyContent="flex-start" alignItems="center" spacing={1}>
								<Grid size="grow">
									<TitleValueDisplay
										title="Default Location"
										value={
											graphProfile.storeNumber
												? graphProfile.storeNumber === 1
													? "Support Office"
													: graphProfile.storeNumber.toString()
												: "Support Office"
										}
										strongTitle
										strongValue={false}
									/>
								</Grid>
							</Grid>
							<Grid>
								<TitleValueDisplay
									title="Country"
									value={
										<span className={classes.countryDisplay}>
											{countryImage}
											{countryCode.toUpperCase()}
										</span>
									}
									strongTitle
									strongValue={false}
								/>
							</Grid>
							<Grid>
								<TitleValueDisplay title="Device IP" value={clientIp} strongTitle strongValue={false} />
							</Grid>
							<Grid>
								<TitleValueDisplay title="Physical Location" value={userLocation} strongTitle strongValue={false} />
							</Grid>
						</Grid>
					</CardContent>
					<CardActions>
						<Grid container justifyContent="center" alignItems="center">
							<Button variant="contained" color="primary" onClick={logout}>
								Sign out
							</Button>
						</Grid>
					</CardActions>
				</Card>
			</Popover>
		</React.Fragment>
	);
});
